import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { ConstantService } from '../shared-component/constant/constant.service';
import { ToasterService } from 'angular2-toaster';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-refer',
  templateUrl: './refer.component.html',
  styleUrls: ['./refer.component.css']
})
export class ReferComponent implements OnInit {

  static readonly STORAGE_KEY = 'referralIdentifier';

  public identifier: string = '';
  public loading: boolean = false;

  private readonly ANDROID_STORE = 'https://play.google.com/store/apps/details?id=com.techfino.credco';
  private readonly IOS_STORE = 'https://apps.apple.com/in/app/techfino-credco/id6763457441';

  private rawHttp: HttpClient;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private handler: HttpBackend,
    private constantService: ConstantService,
    private toasterService: ToasterService
  ) {
    this.rawHttp = new HttpClient(handler);
  }

  ngOnInit() {
    const params = this.route.snapshot.queryParams || {};
    this.identifier = (params['identifier'] || params['referralCode'] || '').trim();

    if (!this.identifier) {
      this.router.navigate(['/authentication/login']);
      return;
    }

    try { localStorage.setItem(ReferComponent.STORAGE_KEY, this.identifier); } catch (_) { }
    this.autoRedirect();
  }

  private autoRedirect() {
    const platform = this.detectPlatform();
    if (platform === 'android') {
      this.openAndroid();
    } else if (platform === 'ios') {
      this.openIOS();
    }
  }

  private detectPlatform(): string {
    const nav: any = window.navigator || {};
    const ua = String(nav.userAgent || '').toLowerCase();
    if (/android/.test(ua)) { return 'android'; }
    if (/iphone|ipad|ipod/.test(ua) || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1)) { return 'ios'; }
    return '';
  }

  openAndroid() {
    this.loading = true;
    window.location.href = `${this.ANDROID_STORE}&referrer=${encodeURIComponent(this.identifier)}`;
  }

  openIOS() {
    // Carry the identifier across the App Store round-trip.
    //
    // Android gets this for free: the redirect endpoint puts sessionId into the
    // Play Install Referrer and the app resolves it on first launch. iOS has no
    // equivalent, so the app reads the clipboard instead
    // (Services/installReferrer.captureClipboardReferral), matching on
    // `?identifier=`. Without this the referral is simply lost on iOS.
    //
    // Written FIRST and synchronously: iOS Safari only honours a clipboard
    // write while the user gesture is still live, and the HTTP call below ends
    // it. Same constraint as opening a window from a click handler.
    this.copyReferralForInstall();

    this.loading = true;
    const base = this.constantService.getUrl(this.constantService.REFER_REDIRECT);
    const url = `${base}?identifier=${encodeURIComponent(this.identifier)}&platform=ios`;
    this.rawHttp.get(url).subscribe(
      (res: any) => {
        window.location.href = (res && res.data && res.data.redirectUrl) || this.IOS_STORE;
      },
      () => { window.location.href = this.IOS_STORE; }
    );
  }

  /**
   * Put the referral link on the clipboard so the freshly installed iOS app can
   * recover the identifier. The `?identifier=` form is what the app's extractor
   * matches, and it stays readable if the customer happens to paste it.
   */
  private copyReferralForInstall(): void {
    if (!this.identifier) { return; }
    const link = `${window.location.origin}${window.location.pathname}`
      + `?identifier=${encodeURIComponent(this.identifier)}`;
    try {
      const nav: any = window.navigator;
      if (nav && nav.clipboard && nav.clipboard.writeText) {
        nav.clipboard.writeText(link).catch(() => this.copyFallback(link));
        return;
      }
      this.copyFallback(link);
    } catch (_) { this.copyFallback(link); }
  }

  /** execCommand path for Safari versions without the async clipboard API. */
  private copyFallback(text: string): void {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-1000px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);   // iOS ignores select() alone
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (_) { /* referral is best-effort; never block the store redirect */ }
  }

  continueWeb() {
    const base = (environment.CREDCO_WEB_URL || '').replace(/\?.*$/, '');
    const sep = base.indexOf('?') >= 0 ? '&' : '?';
    window.location.href = `${base}${sep}ref=${encodeURIComponent(this.identifier)}`;
  }
}
